import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

export default function CodingChallengeLauncher({
  contentId,
  contentData,
  chapterTitle,
  contentTitle,
}) {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const coding = contentData?.codingData || contentData?.coding || contentData || {};
  const totalMarks = Number(coding.totalMarks || 10);
  const autoGrading = coding.autoGradingEnabled !== false;
  const hasPoster = Boolean(
    coding.challengeAttachment?.fileUrl ||
      coding.challengeAttachment?.file_url ||
      coding.challenge_attachment?.fileUrl,
  );

  const openChallenge = () => {
    navigate(`/student/courses/${courseId}/coding/${contentId}`, {
      state: { chapterTitle, contentTitle },
    });
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        borderColor: 'primary.light',
        bgcolor: 'background.paper',
      }}
    >
      <Stack spacing={2} alignItems="stretch">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ width: '100%' }}
        >
          <Box
            sx={{
              width: { xs: 44, sm: 52 },
              height: { xs: 44, sm: 52 },
              borderRadius: 2,
              bgcolor: 'primary.light',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <CodeRoundedIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={700}>
              Coding Challenge Workspace
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {hasPoster
                ? 'Review the challenge poster, then code in a focused full-screen IDE.'
                : 'Open the focused workspace to write, run, and submit your solution.'}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`${totalMarks} marks`} size="small" color="primary" variant="outlined" />
          <Chip
            label={isMobile
              ? (autoGrading ? 'Auto-grade' : 'Manual')
              : (autoGrading ? 'Auto-grading ON' : 'Manual review')}
            size="small"
            color={autoGrading ? 'success' : 'warning'}
            variant="outlined"
          />
          {hasPoster ? (
            <Chip
              label={isMobile ? 'Poster' : 'Challenge poster included'}
              size="small"
              variant="outlined"
            />
          ) : null}
        </Stack>

        <Button
          variant="contained"
          size={isMobile ? 'medium' : 'large'}
          fullWidth={isMobile}
          startIcon={<OpenInNewRoundedIcon />}
          onClick={openChallenge}
          sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
        >
          {hasPoster
            ? (isMobile ? 'Start Challenge' : 'Explore & Start Challenge')
            : (isMobile ? 'Open Workspace' : 'Open Challenge Workspace')}
        </Button>
      </Stack>
    </Paper>
  );
}
